/**
 * Admin API client
 */

import axios from 'axios'
import { attachAuth } from '../../lib/apiAuth'
import type {
  StatsOverview,
  FileUploadResponse,
  ImportResult,
  EntityListResponse,
  EntityType,
  EditionsListResponse,
  EditionInfo,
  ImportExecuteParams,
  ClassificationType,
  ClassificationListItem,
  DataSourcesListResponse,
  DataSourceDetailResponse,
  DataSourceField,
  FieldLabelUpdateRequest,
  FieldLabelResponse,
  EntityLevel,
} from '../types'

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8899'

const api = axios.create({
  baseURL: `${API_BASE}/admin`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// ============================================================================
// Stats
// ============================================================================

export async function getStatsOverview(): Promise<StatsOverview> {
  const response = await api.get<StatsOverview>('/stats/overview')
  return response.data
}

// Edition stats for dashboard history
export interface EditionEntityCounts {
  states: number
  government_districts: number
  counties: number
  municipal_associations: number
  municipalities: number
}

export interface EditionStats {
  id: number
  edition: string
  reference_year: number
  reference_date_display: string
  import_date: string | null
  is_latest: boolean
  entity_counts: EditionEntityCounts
}

export interface EditionStatsResponse {
  editions: EditionStats[]
  selected_edition_id: number | null
}

export async function getEditionStats(): Promise<EditionStatsResponse> {
  const response = await api.get<EditionStatsResponse>('/stats/editions')
  return response.data
}

// Classification data source info
export interface ClassificationDataSource {
  id: number
  provider: string
  dataset_name: string
  edition: string
  reference_year: number
  reference_date_display: string
  import_date: string | null
}

export interface ClassificationStatsResponse {
  data_source: ClassificationDataSource | null
  info: string
}

export async function getClassificationStats(): Promise<ClassificationStatsResponse> {
  const response = await api.get<ClassificationStatsResponse>('/stats/classifications')
  return response.data
}

// ============================================================================
// Entity Lists
// ============================================================================

export interface EntityListParams {
  entityType: EntityType
  limit?: number
  offset?: number
  search?: string
  editionId?: number | null
}

export async function getEntityList<T = Record<string, unknown>>(
  params: EntityListParams
): Promise<EntityListResponse<T>> {
  const { entityType, limit = 50, offset = 0, search, editionId } = params
  const response = await api.get<EntityListResponse<T>>(`/entities/${entityType}`, {
    params: {
      limit,
      offset,
      search: search || undefined,
      edition_id: editionId || undefined,
    },
  })
  return response.data
}

// ============================================================================
// Editions
// ============================================================================

export async function getEditions(dataset?: string): Promise<EditionsListResponse> {
  const response = await api.get<EditionsListResponse>('/editions', {
    params: dataset ? { dataset } : undefined,
  })
  return response.data
}

export async function getEdition(editionId: number): Promise<EditionInfo> {
  const response = await api.get<EditionInfo>(`/editions/${editionId}`)
  return response.data
}

// ============================================================================
// Import
// ============================================================================

export async function uploadGV100Files(
  adFile: File,
  nadFile?: File
): Promise<FileUploadResponse> {
  const formData = new FormData()
  formData.append('ad_file', adFile)
  if (nadFile) {
    formData.append('nad_file', nadFile)
  }

  const response = await api.post<FileUploadResponse>('/import/gv100/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return response.data
}

// Response from execute import - can be either a queued job or direct result
export interface ExecuteImportResponse extends Partial<ImportResult> {
  status: string
  job_id?: string
  message?: string
}

export async function executeGV100Import(
  uploadId: string,
  params: ImportExecuteParams = { mode: 'upsert', edition_mode: 'update_existing' }
): Promise<ExecuteImportResponse> {
  const response = await api.post<ExecuteImportResponse>(`/import/gv100/execute/${uploadId}`, params)
  return response.data
}

// Legacy import endpoints (direct import without upload wizard)
export async function importSeedData(): Promise<{ status: string; message?: string }> {
  const response = await api.post('/import/seed-data')
  return response.data
}

export async function importDestatis(): Promise<ImportResult> {
  const response = await api.post<ImportResult>('/import/destatis')
  return response.data
}

export async function importAll(): Promise<{
  status: string
  message: string
  results: Record<string, unknown>
}> {
  const response = await api.post('/import/all')
  return response.data
}

// ============================================================================
// Classifications (NAD)
// ============================================================================

export interface ClassificationListParams {
  classificationType: ClassificationType
  limit?: number
  offset?: number
  search?: string
}

export async function getClassificationList(
  params: ClassificationListParams
): Promise<EntityListResponse<ClassificationListItem>> {
  const { classificationType, limit = 50, offset = 0, search } = params
  const response = await api.get<EntityListResponse<ClassificationListItem>>(
    `/classifications/${classificationType}`,
    {
      params: { limit, offset, search: search || undefined },
    }
  )
  return response.data
}

export interface ClassificationUpdateData {
  bbsr_code?: string | null
  name?: string | null
  description?: string | null
}

export interface ClassificationUpdateResponse {
  id: number
  code: string
  bbsr_code?: string | null
  name: string
  description?: string | null
  updated_fields: string[]
}

export async function updateClassificationItem(
  classificationType: ClassificationType,
  itemId: number,
  data: ClassificationUpdateData
): Promise<ClassificationUpdateResponse> {
  const response = await api.patch<ClassificationUpdateResponse>(
    `/classifications/${classificationType}/${itemId}`,
    data
  )
  return response.data
}

export function getClassificationExportUrl(classificationType: ClassificationType): string {
  return `${API_BASE}/admin/classifications/${classificationType}/export`
}

export interface ClassificationImportResponse {
  status: string
  updated: number
  skipped: number
  errors: string[]
  total_errors: number
}

export async function importClassificationCsv(
  classificationType: ClassificationType,
  file: File
): Promise<ClassificationImportResponse> {
  const formData = new FormData()
  formData.append('file', file)

  const response = await api.post<ClassificationImportResponse>(
    `/classifications/${classificationType}/import`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  )
  return response.data
}

// ============================================================================
// Jobs
// ============================================================================

export interface JobProgress {
  current: number
  total: number
  percentage: number
  message: string | null
}

export interface JobResponse {
  job_id: string
  job_type: string
  status: 'pending' | 'queued' | 'running' | 'completed' | 'failed' | 'cancelled'
  progress: JobProgress
  created_at: string | null
  started_at: string | null
  completed_at: string | null
  duration_seconds: number | null
  can_rollback: boolean
  error_message: string | null
  result: Record<string, unknown> | null
}

export async function getJobStatus(jobId: string): Promise<JobResponse> {
  const response = await api.get<JobResponse>(`/jobs/${jobId}`)
  return response.data
}

// ============================================================================
// Data Sources
// ============================================================================

export async function getDataSources(entityLevel?: EntityLevel): Promise<DataSourcesListResponse> {
  const response = await api.get<DataSourcesListResponse>('/data-sources', {
    params: entityLevel ? { entity_level: entityLevel } : undefined,
  })
  return response.data
}

export async function getDataSourceDetail(sourceTable: string): Promise<DataSourceDetailResponse> {
  const response = await api.get<DataSourceDetailResponse>(`/data-sources/${sourceTable}`)
  return response.data
}

export async function getFieldLabels(sourceTable: string): Promise<DataSourceField[]> {
  const response = await api.get<DataSourceField[]>(`/field-labels/${sourceTable}`)
  return response.data
}

export async function updateFieldLabel(
  labelId: number,
  data: FieldLabelUpdateRequest
): Promise<FieldLabelResponse> {
  const response = await api.patch<FieldLabelResponse>(`/field-labels/${labelId}`, data)
  return response.data
}

export async function resetFieldLabels(sourceTable: string): Promise<{ reset_count: number }> {
  const response = await api.post<{ reset_count: number }>(`/field-labels/reset/${sourceTable}`)
  return response.data
}

// ============================================================================
// Reference Data (Regionalisierung & Typisierung)
// ============================================================================

export interface ReferenceTableStatus {
  table_name: string
  db_count: number
  csv_exists: boolean
  csv_path: string
  csv_count: number
  is_empty: boolean
  needs_sync: boolean
}

export interface ReferenceDataStatusResponse {
  tables: ReferenceTableStatus[]
  all_tables_have_data: boolean
  csv_files_available: boolean
  empty_tables: string[]
}

export async function getReferenceDataStatus(): Promise<ReferenceDataStatusResponse> {
  const response = await api.get<ReferenceDataStatusResponse>('/reference-data/status')
  return response.data
}

export interface ReferenceDataLoadResult {
  [tableName: string]: {
    inserted?: number
    updated?: number
    skipped?: number
    error?: string
  }
}

export interface ReferenceDataLoadResponse {
  status: string
  message: string
  results: ReferenceDataLoadResult
}

export async function loadReferenceData(tables?: string[]): Promise<ReferenceDataLoadResponse> {
  const response = await api.post<ReferenceDataLoadResponse>('/reference-data/load', null, {
    params: tables?.length ? { tables: tables.join(',') } : undefined,
  })
  return response.data
}

// Basic-Auth-Header + 401-Behandlung (optionaler Schutz, v0.3.8)
attachAuth(api)
